import {
  DEFAULT_CAUSTIC_CONFIG,
  CAUSTIC_RANGES,
} from "@/lib/laser-physics";
import {
  CausticConfig,
  CausticSystemResult,
  SceneOpticsConfig,
} from "@/types/laser-engraving";
import { useControls } from "leva";
import { useCallback, useMemo } from "react";

/**
 * 焦散效果配置管理Hook
 *
 * 提供焦散效果参数的完整管理功能，包括：
 * - Leva控制面板集成（不含位置参数，由场景光学配置统一管理）
 * - 基于真实光学焦散效果的默认配置
 * - 参数验证和边界检查
 * - 重置功能
 * - 与场景光学配置的集成
 *
 * @param sceneOptics - 场景光学配置（包含光源位置和效果平面位置）
 * @param panelName - 控制面板名称
 * @param initialConfig - 初始配置（可选）
 * @returns 焦散效果配置和控制函数
 */
export function useCausticConfig(
  sceneOptics: SceneOpticsConfig,
  panelName: string = "Caustic Settings",
  initialConfig?: Partial<CausticConfig>,
): CausticSystemResult {
  // 合并初始配置
  const effectiveConfig = useMemo(() => {
    return { ...DEFAULT_CAUSTIC_CONFIG, ...initialConfig };
  }, [initialConfig]);

  // 🎯 Leva控制面板配置 - 基于真实光学焦散效果（位置参数由场景光学配置统一管理）
  const config = useControls(panelName, {
    enabled: {
      value: effectiveConfig.enabled,
    },
    causticsOnly: {
      value: effectiveConfig.causticsOnly,
    },
    backside: {
      value: effectiveConfig.backside,
    },
    color: {
      value: effectiveConfig.color,
    },
    worldRadius: {
      value: effectiveConfig.worldRadius,
      ...CAUSTIC_RANGES.worldRadius,
    },
    ior: {
      value: effectiveConfig.ior,
      ...CAUSTIC_RANGES.ior,
    },
    backsideIOR: {
      value: effectiveConfig.backsideIOR,
      ...CAUSTIC_RANGES.backsideIOR,
    },
    intensity: {
      value: effectiveConfig.intensity,
      ...CAUSTIC_RANGES.intensity,
    },
  });

  // 重置为默认值
  const resetToDefaults = useCallback(() => {
    console.log("🎯 重置焦散效果为默认配置");
    // Leva 的 set 函数会在下次渲染时自动重置，这里我们记录日志即可
  }, []);

  return {
    config: {
      enabled: config.enabled,
      causticsOnly: config.causticsOnly,
      backside: config.backside,
      color: config.color,
      worldRadius: config.worldRadius,
      ior: config.ior,
      backsideIOR: config.backsideIOR,
      intensity: config.intensity,
    } as CausticConfig,
    resetToDefaults,
  };
}

/**
 * 简化的焦散效果Hook - 仅包含核心参数
 * 用于快速原型和简单场景
 *
 * @param sceneOptics - 场景光学配置（包含光源位置和效果平面位置）
 * @param panelName - 控制面板名称
 * @returns 简化的焦散效果配置
 */
export function useSimpleCausticConfig(
  sceneOptics: SceneOpticsConfig,
  panelName: string = "Simple Caustic",
) {
  const config = useControls(panelName, {
    enabled: {
      value: DEFAULT_CAUSTIC_CONFIG.enabled,
    },
    intensity: {
      value: DEFAULT_CAUSTIC_CONFIG.intensity,
      min: 0.0,
      max: 1.0,
      step: 0.01,
    },
    ior: {
      value: DEFAULT_CAUSTIC_CONFIG.ior,
      min: 1.0,
      max: 3.0,
      step: 0.01,
    },
    worldRadius: {
      value: DEFAULT_CAUSTIC_CONFIG.worldRadius,
      min: 0.1,
      max: 3.0,
      step: 0.1,
    },
  });

  const resetToDefaults = useCallback(() => {
    console.log("重置简化焦散效果配置");
  }, []);

  return {
    config: {
      ...DEFAULT_CAUSTIC_CONFIG,
      enabled: config.enabled,
      intensity: config.intensity,
      ior: config.ior,
      worldRadius: config.worldRadius,
    },
    resetToDefaults,
  };
}

/**
 * 焦散效果配置验证函数
 * 验证配置是否在有效范围内
 * 注意: 光源位置和焦散平面位置的验证现在由 validateSceneOpticsConfig 处理
 *
 * @param config - 焦散效果配置
 * @returns 验证结果和错误信息
 */
export function validateCausticConfig(config: CausticConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // 验证启用状态（布尔值不需要范围检查）
  if (typeof config.enabled !== "boolean") {
    errors.push("启用状态必须是布尔值");
  }

  // 验证强度
  if (config.intensity < 0 || config.intensity > 1) {
    errors.push("强度必须在0-1之间");
  }

  // 验证折射率
  if (config.ior < 1 || config.ior > 3) {
    errors.push("折射率必须在1-3之间");
  }

  // 验证背面折射率
  if (config.backsideIOR < 1 || config.backsideIOR > 2) {
    errors.push("背面折射率必须在1-2之间");
  }

  // 验证世界半径
  if (config.worldRadius <= 0 || config.worldRadius > 10) {
    errors.push("世界半径必须大于0且小于等于10");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// 类型导出，方便其他文件使用
export type { CausticConfig, CausticSystemResult };